import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ChartLine,
  Eye,
  CursorClick,
  ChatCircle,
  Calendar,
  FileText,
  Trophy,
  CircleNotch,
  PaperPlaneTilt,
  ArrowUp,
  ArrowDown,
  Target,
} from '@phosphor-icons/react';


function KPICard({ icon: Icon, label, value, rate, target, color = 'blue', subtitle }) {
  const hasRate = rate !== undefined && rate !== null;
  const meetsTarget = target !== undefined && rate >= target;
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    violet: 'from-violet-500 to-fuchsia-600',
    amber: 'from-amber-500 to-amber-600',
    rose: 'from-rose-500 to-rose-600',
    cyan: 'from-cyan-500 to-cyan-600',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 relative overflow-hidden" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${colors[color]} opacity-10 rounded-full -mr-12 -mt-12`} />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colors[color]} text-white flex items-center justify-center shadow-sm`}>
            <Icon size={18} weight="fill" />
          </div>
          {hasRate && (
            <div className={`flex items-center gap-1 text-xs font-bold ${meetsTarget ? 'text-emerald-600' : 'text-amber-600'}`}>
              {meetsTarget ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
              {rate}%
            </div>
          )}
        </div>
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {target !== undefined && (
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
            <Target size={10} weight="fill" /> Meta: {target}%
          </p>
        )}
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}


function FunnelRow({ label, count, total, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="font-bold text-slate-900">{count} · {pct.toFixed(1)}%</span>
      </div>
      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


export default function CampaignMetricsPage() {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(7);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAll();
  }, [window, filter]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [s, m] = await Promise.all([
        axios.get(`${API_URL}/api/tracking/stats?days=${window}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tracking/messages?limit=50${filter !== 'all' ? '&status=' + filter : ''}`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setMessages(m.data?.messages || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><CircleNotch size={40} className="animate-spin text-violet-600" /></div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="campaign-metrics-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white flex items-center justify-center">
            <ChartLine size={22} weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Métricas de Campañas</h1>
            <p className="text-sm text-slate-500">Open, click, reply, meeting y conversion rates en tiempo real</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-1">
          {[
            { v: 1, l: '24h' },
            { v: 7, l: '7 días' },
            { v: 30, l: '30 días' },
            { v: 90, l: '90 días' },
          ].map(w => (
            <button
              key={w.v}
              onClick={() => setWindow(w.v)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${window === w.v ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid={`window-${w.v}d-btn`}
            >
              {w.l}
            </button>
          ))}
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={PaperPlaneTilt} label="Emails Enviados" value={stats?.sent || 0} color="blue" subtitle={`Últimos ${window} días`} />
        <KPICard icon={Eye} label="Tasa de Apertura" value={stats?.opens || 0} rate={stats?.open_rate} target={stats?.targets?.open_rate || 40} color="cyan" />
        <KPICard icon={CursorClick} label="Tasa de Click" value={stats?.clicks || 0} rate={stats?.click_rate} color="violet" />
        <KPICard icon={ChatCircle} label="Tasa de Respuesta" value={stats?.replies || 0} rate={stats?.reply_rate} target={stats?.targets?.reply_rate || 3} color="green" />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={ChatCircle} label="Interested" value={stats?.interested || 0} color="green" subtitle="Leads calientes" />
        <KPICard icon={Calendar} label="Meetings Agendadas" value={stats?.meetings_scheduled || 0} rate={stats?.meeting_rate} target={stats?.targets?.meeting_rate || 2} color="amber" />
        <KPICard icon={FileText} label="Propuestas Enviadas" value={stats?.proposals_sent || 0} color="rose" subtitle="En negociación" />
        <KPICard icon={Trophy} label="Cierres Ganados" value={stats?.closed_won || 0} rate={stats?.conversion_rate} color="green" subtitle="Conversion rate" />
      </div>

      {/* Funnel */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Embudo de Conversión</h2>
        <div className="space-y-4">
          <FunnelRow label="Enviados" count={stats?.sent || 0} total={stats?.sent || 1} color="bg-blue-500" />
          <FunnelRow label="Abiertos" count={stats?.opens || 0} total={stats?.sent || 1} color="bg-cyan-500" />
          <FunnelRow label="Click en enlace" count={stats?.clicks || 0} total={stats?.sent || 1} color="bg-violet-500" />
          <FunnelRow label="Respondieron" count={stats?.replies || 0} total={stats?.sent || 1} color="bg-emerald-500" />
          <FunnelRow label="Interesados" count={stats?.interested || 0} total={stats?.sent || 1} color="bg-green-500" />
          <FunnelRow label="Reunión agendada" count={stats?.meetings_scheduled || 0} total={stats?.sent || 1} color="bg-amber-500" />
          <FunnelRow label="Propuesta enviada" count={stats?.proposals_sent || 0} total={stats?.sent || 1} color="bg-rose-500" />
          <FunnelRow label="Cerrado ganado" count={stats?.closed_won || 0} total={stats?.sent || 1} color="bg-emerald-700" />
        </div>
      </div>

      {/* Recent messages table */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-slate-900">Mensajes Recientes</h2>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { v: 'all', l: 'Todos' },
              { v: 'opened', l: 'Abiertos' },
              { v: 'clicked', l: 'Click' },
              { v: 'replied', l: 'Respondidos' },
              { v: 'not_opened', l: 'Sin abrir' },
            ].map(f => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-1 rounded text-xs font-semibold ${filter === f.v ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-600'}`}
                data-testid={`filter-${f.v}-btn`}
              >
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {messages.length === 0 ? (
          <p className="text-center text-slate-400 py-10 text-sm">Aún no hay mensajes enviados en este rango</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Asunto</th>
                  <th className="text-left px-3 py-2">Etapa</th>
                  <th className="text-center px-3 py-2">Opens</th>
                  <th className="text-center px-3 py-2">Clicks</th>
                  <th className="text-center px-3 py-2">Reply</th>
                  <th className="text-left px-3 py-2">Enviado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {messages.map((m) => (
                  <tr key={m.message_id} className="hover:bg-slate-50" data-testid={`msg-row-${m.message_id}`}>
                    <td className="px-3 py-2 text-slate-800 truncate max-w-[200px]">{m.email}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[240px]">{m.subject}</td>
                    <td className="px-3 py-2">
                      <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{m.stage || '—'}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {m.first_open_at ? <span className="text-emerald-600 font-bold">{m.opens_count}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {m.first_click_at ? <span className="text-violet-600 font-bold">{m.clicks_count}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {m.replied ? (
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                          m.reply_intent === 'interested' ? 'bg-emerald-100 text-emerald-700' :
                          m.reply_intent === 'unsubscribe' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {m.reply_intent || 'yes'}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{m.sent_at?.replace('T', ' ').slice(0, 16)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tips card */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
        <h3 className="text-sm font-bold text-indigo-900 mb-2">💡 Benchmarks SOP IQES</h3>
        <ul className="text-sm text-indigo-800 space-y-1 ml-5 list-disc">
          <li>Open rate objetivo: <strong>40%+</strong> (promedio industria B2B: 15-25%)</li>
          <li>Reply rate objetivo: <strong>2-5%</strong> en cold outreach</li>
          <li>Meeting rate objetivo: <strong>1-3%</strong> del total de envíos</li>
          <li>Conversion a cliente: <strong>10-20%</strong> de las meetings</li>
          <li>Si open rate &lt;20%, revisa: líneas de asunto, reputación de dominio, timing de envío</li>
          <li>Si reply rate &lt;1%, revisa: personalización, valor ofrecido, relevancia al target</li>
        </ul>
      </div>
    </div>
  );
}
